import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SystemChipItem } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../hooks/useJobs";
import { useNurses } from "../../../hooks/useNurses";
import type { Nurse } from "../../../utils/models";
import { CHANNEL_LABEL_KEY, CHANNEL_VALUES } from "./activityLogGroupedDisplay";
import { type ActivityLogPerformedByOption, toMissingPerformedByOption, useActivityLogPerformedByOptionsQuery } from "./useActivityLogPerformedByOptionsQuery";

export type GroupedActivityLogFilterOptions = {
	nurseOptions: SystemAutoCompleteOption<Nurse>[];
	/** The chosen department's nurse ids once its own roster request has succeeded; `null` before
	 * that, when a nurse id can't yet be judged unknown. */
	departmentNurseIds: ReadonlySet<number> | null;
	/** The chosen department's roster request failed — a nurse id will never be judged, so nothing
	 * may wait on it. */
	departmentNursesFailed: boolean;
	channelItems: SystemChipItem<string>[];
	shiftJobOptions: DropdownItem<string>[];
	enabledJobIds: ReadonlySet<string>;
	/** True once the department's job configuration is in — before that a job id can't be judged retired. */
	jobsLoaded: boolean;
	performedByOptions: ActivityLogPerformedByOption[];
	performedByOptionsLoading: boolean;
	/** The option values the backend returned — already masked for this viewer, so authoritative — or
	 * `null` until they have loaded. */
	fetchedPerformedByValues: ReadonlySet<string> | null;
};

/**
 * Options come from the provider's roster, as before. Whether a nurse id is unknown is judged from the
 * roster request for THIS department instead (the same `useNurses` cache entry the provider refetches
 * into — `enabled: false`, so observing it sends nothing): the provider keeps the previous
 * department's roster until a refresh lands and skips an empty one, so the roster's contents can't say
 * which department they belong to, and a department with no nurses would never count as loaded.
 */
const useNurseOptions = (): Pick<GroupedActivityLogFilterOptions, "nurseOptions" | "departmentNurseIds" | "departmentNursesFailed"> => {
	const { hospitalData, getNurseFormattedName, chosenDepartment } = useHospitalData();
	const { data: departmentNurses, isSuccess, isError: departmentNursesFailed } = useNurses(chosenDepartment.department_id);
	const nurseOptions = useMemo(
		() => hospitalData.nurses.map((nurse) => ({ label: getNurseFormattedName(nurse.id), value: nurse })).sort((a, b) => a.label.localeCompare(b.label)),
		[hospitalData.nurses, getNurseFormattedName],
	);
	const departmentNurseIds = useMemo(() => (isSuccess ? new Set(departmentNurses.map((nurse) => nurse.id)) : null), [isSuccess, departmentNurses]);
	return { nurseOptions, departmentNurseIds, departmentNursesFailed };
};

/** Same department-enabled jobs list MasterSchedule v2's job filter uses. */
const useShiftJobOptions = (): Pick<GroupedActivityLogFilterOptions, "shiftJobOptions" | "enabledJobIds" | "jobsLoaded"> => {
	const { chosenDepartment } = useHospitalData();
	const shiftJobs = useJobs();
	const jobsLoaded = chosenDepartment.jobIds !== undefined && chosenDepartment.jobs !== undefined;
	return useMemo(
		() => ({
			shiftJobOptions: shiftJobs.map((job) => ({ value: job.id, label: job.displayName })),
			enabledJobIds: new Set(shiftJobs.map((job) => job.id)),
			jobsLoaded,
		}),
		[shiftJobs, jobsLoaded],
	);
};

/**
 * A selected Performed By value the fetched options don't carry (a shared link, a user with no
 * activity here any more) is still shown so it can be seen and removed — same approach as the Team
 * page's `buildSourceListOptions` — masked for a non-support viewer (see `toMissingPerformedByOption`).
 */
const usePerformedByOptions = (
	selectedPerformedBy: string[],
): Pick<GroupedActivityLogFilterOptions, "performedByOptions" | "performedByOptionsLoading" | "fetchedPerformedByValues"> => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	// DEFAULT: no user info yet reads as non-support — the side that masks, so nothing is revealed.
	const isSupport = !!userInfo?.support;
	const { data, isLoading } = useActivityLogPerformedByOptionsQuery();
	const fetchedPerformedByValues = useMemo(() => (data ? new Set(data.map((option) => option.value)) : null), [data]);
	const performedByOptions = useMemo(() => {
		// DEFAULT: no options yet (loading or failed) — the selected values below still render.
		const fetched = data ?? [];
		const missing = selectedPerformedBy
			.filter((value) => !fetchedPerformedByValues?.has(value))
			.map((value) => toMissingPerformedByOption(value, isSupport, t));
		return missing.length > 0 ? [...fetched, ...missing] : fetched;
	}, [data, fetchedPerformedByValues, selectedPerformedBy, isSupport, t]);
	return { performedByOptions, performedByOptionsLoading: isLoading, fetchedPerformedByValues };
};

/** Every option list the Grouped Activity Log's filters resolve against. */
export function useGroupedActivityLogFilterOptions({ selectedPerformedBy }: { selectedPerformedBy: string[] }): GroupedActivityLogFilterOptions {
	const { t } = useTranslation();
	const channelItems = useMemo<SystemChipItem<string>[]>(() => CHANNEL_VALUES.map((value) => ({ value, label: t(CHANNEL_LABEL_KEY[value]) })), [t]);
	return { ...useNurseOptions(), ...useShiftJobOptions(), ...usePerformedByOptions(selectedPerformedBy), channelItems };
}
